import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Easing,
  Img,
  useDelayRender,
  useRemotionEnvironment,
  interpolate,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import { useStablePreferredLocalUrl } from '../../utils/find-asset-by-id';
import { getRectAfterCrop } from '../../utils/get-dimensions-after-crop';
import { useAssetFromItem } from '../../utils/use-context';
import { useCroppableLayer } from '../croppable-layer';
import { ZoomWrapper } from '../zoom-wrapper';
import {
  calculateAnimationsStyle,
  mergeAnimationStyle,
} from '../animation/calculate-animation';
import {
  calculateFadeInOpacity,
  calculateFadeOutOpacity,
} from '../video/calculate-fade';
import { DocumentItem } from './pdf-item-type';
import {
  DEFAULT_PAGE_TRANSITION_DURATION_IN_SECONDS,
  getPageTransitionAt,
} from './page-keyframes';
import { renderPdfPageToUrl } from './pdf-page-renderer';

/**
 * pdf/ppt 아이템 레이어 — PdfAsset(문서)의 페이지를 pdf.js 로 래스터해 이미지처럼
 * 렌더한다. 구조는 image-layer 미러(useCroppableLayer + ZoomWrapper + 애니메이션 +
 * 페이드).
 *
 * 페이지 타임라인(pageKeyframes) 경계에서는 종이 넘김 전환 — 나가는 페이지를
 * 왼쪽 축 3D 플립(perspective + rotateY + backface hidden)으로 젖히고, 접힘
 * 그림자·하이라이트 그라데이션으로 말림을 근사한다. 진짜 종이 말림(메시 변형)은
 * WebGL 이 필요해 제외 — ponytail: 필요해지면 캔버스 기반 curl 로 업그레이드.
 *
 * 페이지 래스터는 비동기라 delayRender 로 export 프레임 캡처 전에 완료를 보장한다
 * (animated-webp 패턴 — effect run 별 독립 handle + cleanup 에서 반드시 release).
 */
const PdfItemUnmemoized: React.FC<{
  item: DocumentItem;
  cropBackground: boolean;
  zIndex?: number;
}> = ({ item, cropBackground, zIndex }) => {
  if (item.type !== 'pdf' && item.type !== 'ppt') {
    throw new Error('Item is not a document');
  }

  const frame = useCurrentFrame();
  const environment = useRemotionEnvironment();
  const {delayRender,continueRender,cancelRender} = useDelayRender();
  const { fps } = useVideoConfig();
  const asset = useAssetFromItem(item);
  const src = useStablePreferredLocalUrl(asset);

  const transitionDurationInFrames = Math.round(
    (item.pageTransitionDurationInSeconds ??
      DEFAULT_PAGE_TRANSITION_DURATION_IN_SECONDS) * fps,
  );

  const pageState = useMemo(
    () =>
      getPageTransitionAt(
        item.pageKeyframes ?? [],
        item.pageIndex,
        frame,
        transitionDurationInFrames,
      ),
    [item.pageKeyframes, item.pageIndex, frame, transitionDurationInFrames],
  );

  // 래스터 완료된 페이지 URL — src 가 바뀌면 리셋. ref 가 SoT, state 는 리렌더 트리거.
  const loadedPagesRef = useRef<{
    src: string | null;
    urls: Record<number, string>;
  }>({ src: null, urls: {} });
  const [, setLoadedVersion] = useState(0);
  if (loadedPagesRef.current.src !== src) {
    loadedPagesRef.current = { src, urls: {} };
  }

  // 키프레임 페이지 선행 래스터 — 경계 도달 전에 준비(프리뷰 끊김 방지). 실패는
  // 필요 시점의 delayRender effect 가 다시 처리하므로 조용히 무시.
  useEffect(() => {
    if (!src) return;
    const pages = new Set<number>([
      item.pageIndex,
      ...(item.pageKeyframes ?? []).map((k) => k.pageIndex),
    ]);
    pages.forEach((pageIndex) => {
      renderPdfPageToUrl(src, pageIndex).catch(() => {});
    });
  }, [src, item.pageIndex, item.pageKeyframes]);

  // 현재 프레임에 필요한 페이지(전환 중엔 2장) 로드 — export 프레임 보장.
  const neededPages = useMemo(() => {
    const pages = [pageState.pageIndex];
    if (pageState.transition) {
      pages.push(pageState.transition.fromPageIndex);
    }
    return pages;
  }, [pageState]);
  const neededPagesKey = neededPages.join(',');

  // preview 실패 재시도 — neededPagesKey 가 안 바뀌어도 다시 시도할 트리거.
  const [retryNonce, setRetryNonce] = useState(0);
  const retryCountRef = useRef(0);

  useEffect(() => {
    if (!src) {
      return;
    }
    const missing = neededPages.filter(
      (pageIndex) => loadedPagesRef.current.urls[pageIndex] === undefined,
    );
    if (missing.length === 0) {
      return;
    }

    let aborted = false;
    let handleContinued = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const handle = delayRender(
      `pdf page render: ${item.id} p.${neededPagesKey}`,
    );

    const safeContinueRender = () => {
      if (!handleContinued) {
        handleContinued = true;
        continueRender(handle);
      }
    };

    // allSettled — 일부 페이지 실패가 성공분 반영을 막지 않게 한다
    Promise.allSettled(
      missing.map(async (pageIndex) => {
        const url = await renderPdfPageToUrl(src, pageIndex);
        return { pageIndex, url };
      }),
    ).then((results) => {
      if (aborted) {
        safeContinueRender();
        return;
      }
      const fulfilled = results.filter(
        (r): r is PromiseFulfilledResult<{ pageIndex: number; url: string }> =>
          r.status === 'fulfilled',
      );
      const firstFailure = results.find(
        (r): r is PromiseRejectedResult => r.status === 'rejected',
      );

      if (fulfilled.length > 0 && loadedPagesRef.current.src === src) {
        fulfilled.forEach(({ value }) => {
          loadedPagesRef.current.urls[value.pageIndex] = value.url;
        });
        setLoadedVersion((v) => v + 1);
      }

      if (!firstFailure) {
        retryCountRef.current = 0;
        safeContinueRender();
        return;
      }

      const error =
        firstFailure.reason instanceof Error
          ? firstFailure.reason
          : new Error(String(firstFailure.reason));
      console.error('[PdfLayer] 페이지 래스터 실패:', error.message, {
        src: src.slice(0, 120),
        pages: neededPagesKey,
      });
      // export 환경: 빈 프레임 캡처 방지를 위해 명시 실패.
      // preview 환경: handle 해제 후 제한 재시도 — 일시 오류로 인한 영구 블랭크 방지.
      if (environment.isRendering || environment.isClientSideRendering) {
        cancelRender(error);
        return;
      }
      safeContinueRender();
      if (retryCountRef.current < 3) {
        retryCountRef.current += 1;
        retryTimer = setTimeout(() => setRetryNonce((v) => v + 1), 1000);
      }
    });

    return () => {
      aborted = true;
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
      }
      safeContinueRender();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, neededPagesKey, item.id, retryNonce, environment.isRendering, environment.isClientSideRendering, delayRender, continueRender, cancelRender]);

  const opacity = useMemo(() => {
    const inOpacity = calculateFadeInOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeInDurationInSeconds: item.fadeInDurationInSeconds,
      framesPerSecond: fps,
    });
    const outOpacity = calculateFadeOutOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeOutDurationInSeconds: item.fadeOutDurationInSeconds,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
    });
    return inOpacity * outOpacity * item.opacity;
  }, [
    item.visualFadeWindow,
    item.fadeInDurationInSeconds,
    fps,
    frame,
    item.opacity,
    item.durationInFrames,
    item.fadeOutDurationInSeconds,
  ]);

  const { innerStyle, outerStyle } = useCroppableLayer({
    item,
    rotation: item.rotation,
    opacity,
    borderRadius: item.borderRadius,
    cropBackground,
    zIndex,
  });

  const animationStyle = useMemo(() => {
    const hasAnimations = item.animations && item.animations.length > 0;
    const hasPositionKeyframes =
      item.positionKeyframes && item.positionKeyframes.length > 0;
    if (!hasAnimations && !hasPositionKeyframes) {
      return {};
    }
    return calculateAnimationsStyle({
      animations: item.animations ?? [],
      currentFrame: frame,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
      positionKeyframes: item.positionKeyframes,
    });
  }, [
    item.animations,
    item.positionKeyframes,
    frame,
    fps,
    item.durationInFrames,
  ]);

  const finalOuterStyle = useMemo(
    () => mergeAnimationStyle(outerStyle, animationStyle),
    [outerStyle, animationStyle],
  );

  const currentUrl = loadedPagesRef.current.urls[pageState.pageIndex] ?? null;
  const fromUrl = pageState.transition
    ? (loadedPagesRef.current.urls[pageState.transition.fromPageIndex] ?? null)
    : null;

  const eased = pageState.transition
    ? interpolate(pageState.transition.progress, [0, 1], [0, 1], {
        easing: Easing.inOut(Easing.ease),
      })
    : 0;
  // 접힘 음영은 플립 중간(90°)에서 가장 진하다
  const foldShade = Math.sin(Math.PI * eased);
  // 역방향(이전 페이지로) 넘김은 반대쪽(오른쪽 축)으로 젖힌다
  const isBackward = pageState.transition
    ? pageState.transition.fromPageIndex > pageState.pageIndex
    : false;
  // 원근 기준은 크롭 후 실제 표시 폭 — 크롭 비율이 커도 깊이감이 일정하게
  const displayWidth = getRectAfterCrop(item).width;

  return (
    <div style={finalOuterStyle}>
      <ZoomWrapper
        itemId={item.id}
        zoomKeyframes={cropBackground ? undefined : item.zoomKeyframes}
        frame={frame}
      >
        {/* 들어오는(현재) 페이지 */}
        {currentUrl ? (
          <Img
            style={innerStyle}
            src={currentUrl}
          />
        ) : null}

        {pageState.transition && fromUrl ? (
          <>
            {/* 들어오는 페이지 위 접힘 그림자 — 넘김이 끝나며 걷힌다 */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                background: `linear-gradient(${isBackward ? 'to left' : 'to right'}, rgba(0,0,0,0.45), rgba(0,0,0,0) 55%)`,
                opacity: (1 - eased) * 0.9,
              }}
            />
            {/* 나가는 페이지 — 접히는 축 기준 3D 플립 (정방향 왼쪽, 역방향 오른쪽) */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                transformOrigin: isBackward ? 'right center' : 'left center',
                transform: `perspective(${Math.max(600, displayWidth * 2)}px) rotateY(${(isBackward ? 180 : -180) * eased}deg)`,
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
              }}
            >
              <Img
                style={innerStyle}
                src={fromUrl}
              />
              {/* 종이 말림 음영·하이라이트 — 들릴수록 진해졌다 사라진다 */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  background: `linear-gradient(${isBackward ? 'to left' : 'to right'}, rgba(0,0,0,0.35), rgba(255,255,255,0.10) 45%, rgba(0,0,0,0.30))`,
                  opacity: foldShade,
                }}
              />
            </div>
          </>
        ) : null}
      </ZoomWrapper>
    </div>
  );
};

export const PdfLayer = React.memo(PdfItemUnmemoized);
